import React, { useEffect, useState } from 'react';
import { cn } from '@utils/index';
import { StripeBanksElementForm } from './StripeBanksElementForm';
import { StripeACHForm } from './StripeACHForm';
import { usePaymentStore } from '../../store/payment';
import { usePlan } from '@features/onboarding/hooks/usePlan';

type PaymentStepProps = {
  /** callback чтобы Finish перешёл к следующему шагу */
  onSubmit: () => void;
  /** функция, позволяющая Finish задать классы внешнему контейнеру */
  setContainerClassName?: (className: string) => void;
};

/**
 * Комбинированный шаг оплаты: слева текстовое сопровождение, справа – форма ACH.
  */
export const PaymentStep: React.FC<PaymentStepProps> = ({ onSubmit }) => {
  const { paymentMethodId } = usePaymentStore();
  const { totalPlanPrice } = usePlan();
  const [currentStep, setCurrentStep] = useState<'connect' | 'submit'>('connect');

  // Автоматически переключаемся на второй шаг когда банк подключен
  useEffect(() => {
    if (paymentMethodId) {
      setCurrentStep('submit');
    }
  }, [paymentMethodId]);

  const monthlyPremium = totalPlanPrice || 1102.01; // Fallback value

  return (
    <div className="bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Заголовок с суммой */}
        <div className="text-center mb-6 sm:mb-8 mt-2">
          <h1 className="text-lg sm:text-xl text-gray-900 font-semibold">
            Your month premium: ${monthlyPremium.toFixed(2)}
          </h1>
        </div>

        {/* Основная карточка */}
        <div className="bg-[#f7f6f2] rounded-2xl p-4 sm:p-8">
          {/* Инструкция */}
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-base sm:text-lg text-primary font-semibold">
              {currentStep === 'connect'
                && 'Setting up your payment is simple. Just connect to your bank and then submit payment.'
              }
            </p>
          </div>

          {/* Прогресс бар с двумя шагами */}
          <div className="flex items-center justify-center mb-8 w-full px-4 sm:px-8">
            <div className="bg-[#f1ede7] rounded-xl p-4 w-full">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Шаг 1: Connect to bank */}
                <div className={cn(
                  "flex items-center justify-center space-x-3 px-4 sm:px-6 py-3 w-full sm:w-auto",
                  currentStep === 'connect'
                    ? "text-primary"
                    : "text-gray-500"
                )}>
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0",
                    currentStep === 'connect'
                      ? "bg-primary text-white"
                      : "bg-gray-400 text-white"
                  )}>
                    1
                  </div>
                  <span className={cn(
                    "font-medium text-center sm:text-left",
                    currentStep === 'connect' ? "font-bold text-primary text-lg sm:text-xl" : "font-normal text-gray-500 text-lg sm:text-xl"
                  )}>
                    Connect to bank
                  </span>
                </div>

                {/* Разделитель */}
                <div className="hidden sm:block w-px h-10 bg-gray-300 mx-2"></div>
                <div className="sm:hidden w-full h-px bg-gray-300"></div>

                {/* Шаг 2: Submit payment */}
                <div className={cn(
                  "flex items-center justify-center space-x-3 px-4 sm:px-6 py-3 w-full sm:w-auto",
                  currentStep === 'submit'
                    ? "text-primary"
                    : "text-gray-500"
                )}>
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0",
                    currentStep === 'submit'
                      ? "bg-primary text-white"
                      : "bg-[#cbbfb1] text-white"
                  )}>
                    2
                  </div>
                  <span className={cn(
                    "font-medium text-center sm:text-left",
                    currentStep === 'submit' ? "font-bold text-primary text-lg sm:text-xl" : "font-bold text-[#cbbfb1] text-lg sm:text-xl"
                  )}>
                    Submit payment
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-6 sm:mb-8">
            <p className="text-base sm:text-lg text-primary font-semibold">
              {currentStep !== 'connect' && <span className="text-primary font-semibold">Bank connected! Click SUBMIT to finalize your payment.</span>}
            </p>
          </div>

          {/* Форма или подтверждение */}
          <div className="flex flex-col justify-center">
            <div className="w-fit max-w-2xl m-auto">
              <StripeBanksElementForm setCurrentStep={setCurrentStep} />
            </div>
            {/* {currentStep !== 'connect' && (
              <div className="w-full max-w-2xl">
                <div className="mt-6 text-center">
                  <button
                    onClick={onSubmit}
                    className="bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};
